/**
 * Confirmation dialog for admin-initiated lifecycle changes from the
 * /admin/users actions menu:
 *
 *  - approve   pending  → active     (audited as ``signup_approved``)
 *  - reject    pending  → rejected   (audited as ``signup_rejected``)
 *  - disable   active   → disabled   (audited as ``account_disabled``)
 *  - enable    disabled → active     (audited as ``account_enabled``)
 *  - restore   rejected → active     (audited as ``signup_approved``)
 *
 * Approve / reject used to live on a dedicated Pending Approvals tab;
 * they were folded into this dialog when that tab was retired and the
 * pending sign-ups surfaced as a status filter on the Users list.
 */

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import RestoreIcon from '@mui/icons-material/Restore';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ComponentType } from 'react';

import { updateUserStatus, type UserListItem } from '../../api/admin';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export type StatusUpdateMode = 'approve' | 'reject' | 'disable' | 'enable' | 'restore';

interface StatusUpdateDialogProps {
  open: boolean;
  user: UserListItem;
  mode: StatusUpdateMode;
  onClose: () => void;
}

interface ModeCopy {
  Icon: ComponentType<{ sx?: object }>;
  title: string;
  body: string;
  confirmLabel: string;
  /** Status to PATCH on the server. */
  targetStatus: 'disabled' | 'active' | 'rejected';
  /** Tone for the highlight summary band. */
  tone: 'warning' | 'success' | 'info' | 'danger';
}

const COPY: Record<StatusUpdateMode, ModeCopy> = {
  approve: {
    Icon: CheckCircleOutlineIcon,
    title: 'Approve this sign-up?',
    body: 'The user can sign in immediately with the password they already set during sign-up, and their current role assignments take effect. If you need to add or change roles, do that first via "Edit Roles", then come back here.',
    confirmLabel: 'Approve sign-up',
    targetStatus: 'active',
    tone: 'success',
  },
  reject: {
    Icon: DoDisturbAltIcon,
    title: 'Reject this sign-up?',
    body: 'The user will not be able to sign in. Their account row is preserved (so the rejection is auditable) and you can reverse this later via the Restore action on the Users list.',
    confirmLabel: 'Reject sign-up',
    targetStatus: 'rejected',
    tone: 'danger',
  },
  disable: {
    Icon: BlockIcon,
    title: 'Disable this account?',
    body: 'The user will be signed out of any active session and won’t be able to sign back in. Their data, role assignments, and audit history are preserved — you can re-enable them at any time.',
    confirmLabel: 'Disable account',
    targetStatus: 'disabled',
    tone: 'warning',
  },
  enable: {
    Icon: VerifiedUserOutlinedIcon,
    title: 'Re-enable this account?',
    body: 'The user can sign in again immediately with their existing password and role assignments.',
    confirmLabel: 'Re-enable',
    targetStatus: 'active',
    tone: 'success',
  },
  restore: {
    Icon: RestoreIcon,
    title: 'Restore this rejected account?',
    body: 'This reverses the earlier rejection. The user will be able to sign in with their existing password (if they had already set one) and any role assignments they had at rejection time are restored. The action is logged as a sign-up approval in the audit trail.',
    confirmLabel: 'Restore account',
    targetStatus: 'active',
    tone: 'info',
  },
};

const TONE_BG: Record<ModeCopy['tone'], { bg: string; border: string; fg: string }> = {
  warning: {
    bg: 'rgba(187, 140, 63, 0.08)',
    border: 'rgba(187, 140, 63, 0.28)',
    fg: '#8e6a2a',
  },
  success: {
    bg: 'rgba(34, 134, 90, 0.08)',
    border: 'rgba(34, 134, 90, 0.28)',
    fg: '#22865a',
  },
  info: {
    bg: 'rgba(50, 110, 184, 0.08)',
    border: 'rgba(50, 110, 184, 0.28)',
    fg: '#326eb8',
  },
  danger: {
    bg: 'rgba(178, 47, 47, 0.08)',
    border: 'rgba(178, 47, 47, 0.30)',
    fg: '#b22f2f',
  },
};

export function StatusUpdateDialog({ open, user, mode, onClose }: StatusUpdateDialogProps) {
  const queryClient = useQueryClient();
  const copy = COPY[mode];
  const tone = TONE_BG[copy.tone];

  const mutation = useMutation({
    mutationFn: () => updateUserStatus(user.id, { status: copy.targetStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'metrics'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'relative',
            bgcolor: IVORY_LIGHT,
            backgroundImage: 'none',
            border: '1px solid rgba(187, 140, 63, 0.22)',
            borderRadius: 2,
            m: { xs: 2, sm: 4 },
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
              zIndex: 1,
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pt: 2.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: tone.bg,
            color: tone.fg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <copy.Icon sx={{ fontSize: 18 }} />
        </Box>
        {copy.title}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ borderColor: 'rgba(187, 140, 63, 0.22)', bgcolor: IVORY_LIGHT }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1.25,
            bgcolor: tone.bg,
            border: `1px solid ${tone.border}`,
            mb: 2,
          }}
        >
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 600, color: INK }}>
            {user.first_name} {user.last_name}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(15, 10, 7, 0.6)' }}>
            {user.email}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 13.5,
            color: 'rgba(15, 10, 7, 0.72)',
            lineHeight: 1.55,
          }}
        >
          {copy.body}
        </Typography>
        {mutation.error && (
          <Alert severity="error" sx={{ mt: 2, fontFamily: FONT_INTER, borderRadius: 1.25 }}>
            {(mutation.error as Error).message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={
            mode === 'reject' ? dangerButtonSx : mode === 'disable' ? warningButtonSx : goldButtonSx
          }
        >
          {mutation.isPending ? 'Working…' : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const goldButtonSx = {
  background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
  color: INK,
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2.5,
  py: 0.85,
  boxShadow: 'none',
  borderRadius: 1,
  '&:hover': {
    background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
    boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
  },
  '&.Mui-disabled': {
    background: 'rgba(15, 10, 7, 0.08)',
    color: 'rgba(15, 10, 7, 0.3)',
  },
} as const;

const dangerButtonSx = {
  bgcolor: '#b22f2f',
  color: '#fff',
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2.5,
  py: 0.85,
  boxShadow: 'none',
  borderRadius: 1,
  '&:hover': {
    bgcolor: '#911e1e',
    boxShadow: '0 4px 14px rgba(178, 47, 47, 0.22)',
  },
  '&.Mui-disabled': {
    bgcolor: 'rgba(15, 10, 7, 0.08)',
    color: 'rgba(15, 10, 7, 0.3)',
  },
} as const;

const warningButtonSx = {
  bgcolor: '#a07530',
  color: '#fff',
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2.5,
  py: 0.85,
  boxShadow: 'none',
  borderRadius: 1,
  '&:hover': {
    bgcolor: '#8e6a2a',
    boxShadow: '0 4px 14px rgba(160, 117, 48, 0.22)',
  },
  '&.Mui-disabled': {
    bgcolor: 'rgba(15, 10, 7, 0.08)',
    color: 'rgba(15, 10, 7, 0.3)',
  },
} as const;

const cancelButtonSx = {
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2,
  color: 'rgba(15, 10, 7, 0.6)',
  '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.04)' },
} as const;
